import { Controller } from "stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller {
  static targets = ["durationHours", "durationMinutes", "durationTotal"];
  static values = {
    sessionDurationMinutes: Number
  };

  async showQuickLook(event) {
    event.preventDefault();

    const attendanceId = this.data.get("attendanceId");
    const durationInput = document.getElementById(`attendance_duration_${attendanceId}`);
    const durationMinutes = durationInput ? durationInput.value : null;
    const notesInput = document.getElementById(`attendance_notes_input_${attendanceId}`);
    const notesValue = notesInput ? notesInput.value : null;

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content;

    const params = new URLSearchParams();
    if (durationMinutes !== null) {
      params.append("duration_in_minutes", durationMinutes);
    }
    if (notesValue !== null) {
      params.append("notes", notesValue);
    }
    const query = params.toString() ? `?${params.toString()}` : "";
    const response = await fetch(`/schools/classroom/sessions/attendance/${attendanceId}/quick-look${query}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken,
        "Accept": "text/vnd.turbo-stream.html"
      }
    });

    if (response.ok) {
      const quickViewElement =
        document.querySelector("#school-classroom-session-attendance-quick-look") ||
        document.querySelector("#school_classrooms_quick_look");
      
      if (!quickViewElement) {
        console.error("Quick view element not found in the DOM.");
        return;
      }

      quickViewElement.classList.remove("u-hidden");

      const responseBody = await response.text();
      if (Turbo && typeof Turbo.renderStreamMessage === "function") {
        Turbo.renderStreamMessage(responseBody);
      } else {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(responseBody, "text/html");
        const template = xmlDoc.querySelector("template");

        if (template) {
          quickViewElement.innerHTML = "";
          quickViewElement.appendChild(template.content.cloneNode(true));
        }
      }
    }
  }
  
  closeQuickLook(event) {
    event.preventDefault();
    
    const quickViewElement =
      document.querySelector("#school-classroom-session-attendance-quick-look") ||
      document.querySelector("#school_classrooms_quick_look");

    if (!quickViewElement) {
      console.error("Quick view element not found in the DOM.");
      return;
    }

    quickViewElement.classList.add("u-hidden");
  }

  updateDuration() {
    if (!this.hasDurationHoursTarget || !this.hasDurationMinutesTarget || !this.hasDurationTotalTarget) {
      return;
    }

    const hours = parseInt(this.durationHoursTarget.value || "0", 10);
    const minutes = parseInt(this.durationMinutesTarget.value || "0", 10);
    const totalMinutes = (hours * 60) + minutes;

    this.durationTotalTarget.value = totalMinutes;
  }

  validate(event) {
    const errors = this.validateForm();
    
    if (errors.length > 0) {
      event.preventDefault();
      this.showValidationBlock(errors.join("<br>"));
      return;
    }    
  }

  validateForm() {
    let validationErrors = [];

    if (!this.hasDurationTotalTarget) {
      validationErrors.push("Duration is required.");
      return validationErrors;
    }

    const totalMinutes = parseInt(this.durationTotalTarget.value || "", 10);
    if (Number.isNaN(totalMinutes) || totalMinutes < 0) {
      validationErrors.push("Duration must be a valid number of minutes.");
      return validationErrors;
    }

    const maxMinutes = parseInt(this.durationTotalTarget.dataset.maxMinutes || "", 10);
    if (!Number.isNaN(maxMinutes) && maxMinutes > 0 && totalMinutes > maxMinutes) {
      validationErrors.push("Duration cannot exceed the session length.");
    }
    
    return validationErrors;
  }

  showValidationBlock(message) {
    const validationBlock = document.getElementById("validation-block");
    const validationMessage = document.getElementById("validation-message");
  
    validationMessage.innerHTML = message;
    validationBlock.hidden = false;
    validationMessage.hidden = false;
  }

  toggleAttendance(event) {
    const checkbox = event.target;
    if (!checkbox) {
      return;
    }

    const attendanceId = checkbox.dataset.attendanceId;
    if (!attendanceId) {
      return;
    }

    this.setDurationForCheckbox(checkbox);
  }

  setDurationForCheckbox(checkbox) {
    const attendanceId = checkbox.dataset.attendanceId;
    if (!attendanceId) {
      return;
    }

    const durationInput = document.getElementById(`attendance_duration_${attendanceId}`);
    if (!durationInput) {
      return;
    }

    const sessionMinutes = Number.isFinite(this.sessionDurationMinutesValue)
      ? this.sessionDurationMinutesValue
      : 0;

    durationInput.value = checkbox.checked ? sessionMinutes : 0;

    const displayElement = document.getElementById(`attendance_duration_display_${attendanceId}`);
    if (displayElement) {
      displayElement.textContent = this.formatDuration(durationInput.value);
    }
  }

  formatDuration(durationMinutes) {
    const total = parseInt(durationMinutes || "0", 10);
    if (Number.isNaN(total) || total === 0) {
      return "-";
    }

    const hours = Math.floor(total / 60);
    const minutes = total % 60;

    if (hours === 0) {
      return `${minutes}m`;
    }
    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}m`;
  }
}
